/**
 * Calendar utility functions using native Date + Intl APIs.
 * No external date libraries - per spec requirement.
 *
 * Key design decisions:
 * - Uses Intl.DateTimeFormat.formatToParts() for timezone-aware YYYY-MM-DD strings
 * - Avoids toISOString() which converts to UTC and can shift dates
 * - Week starts on Sunday (hardcoded for Phase 4)
 */

export interface CalendarDay {
  date: Date;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isPast: boolean;
}

export interface MonthData {
  year: number;
  month: number; // 0-indexed (January = 0)
  days: CalendarDay[];
  monthName: string;
}

/**
 * Get the browser's default timezone.
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format a Date to YYYY-MM-DD in the specified timezone.
 * Uses formatToParts() to avoid UTC day-shift issues from toISOString().
 */
export function toDateString(date: Date, timezone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  // en-CA locale uses YYYY-MM-DD format natively
  return formatter.format(date);
}

/**
 * Format a Date to YYYY-MM for the available-dates API.
 */
export function toMonthString(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);

  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';

  return `${year}-${month}`;
}

/**
 * Get the current date at midnight in the specified timezone.
 */
export function getTodayInTimezone(timezone: string): Date {
  const now = new Date();
  const todayStr = toDateString(now, timezone);
  // Parse YYYY-MM-DD to create a Date at local midnight
  const [year, month, day] = todayStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Check if a date is today in the specified timezone.
 */
export function isToday(date: Date, timezone: string): boolean {
  const today = getTodayInTimezone(timezone);
  return isSameDay(date, today);
}

/**
 * Check if a date is in the past (before today) in the specified timezone.
 */
export function isPastDate(date: Date, timezone: string): boolean {
  const today = getTodayInTimezone(timezone);
  return date < today;
}

/**
 * Check if two dates are the same calendar day.
 */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Check if a month is the current month in the specified timezone.
 */
export function isCurrentMonth(year: number, month: number, timezone: string): boolean {
  const today = getTodayInTimezone(timezone);
  return today.getFullYear() === year && today.getMonth() === month;
}

/**
 * Check if a month is in the past (before current month).
 */
export function isPastMonth(year: number, month: number, timezone: string): boolean {
  const today = getTodayInTimezone(timezone);
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();

  if (year < todayYear) return true;
  if (year === todayYear && month < todayMonth) return true;
  return false;
}

/**
 * Generate calendar grid for a given month.
 * Includes trailing days from prev month and leading days from next month
 * to fill complete weeks (starting Sunday).
 */
export function generateMonthData(
  year: number,
  month: number,
  timezone: string,
): MonthData {
  const today = getTodayInTimezone(timezone);

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();

  // Day of week for first day (0 = Sunday)
  const startDayOfWeek = firstDayOfMonth.getDay();

  // Days from previous month to show
  const prevMonthLastDay = new Date(year, month, 0).getDate();

  const days: CalendarDay[] = [];

  // Previous month's trailing days
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const day = prevMonthLastDay - i;
    const date = new Date(year, month - 1, day);
    days.push({
      date,
      dayOfMonth: day,
      isCurrentMonth: false,
      isToday: false,
      isPast: date < today,
    });
  }

  // Current month's days
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    days.push({
      date,
      dayOfMonth: day,
      isCurrentMonth: true,
      isToday: isSameDay(date, today),
      isPast: date < today,
    });
  }

  // Next month's leading days to complete final row
  const totalCells = days.length;
  const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
  for (let day = 1; day <= remainingCells; day++) {
    const date = new Date(year, month + 1, day);
    days.push({
      date,
      dayOfMonth: day,
      isCurrentMonth: false,
      isToday: false,
      isPast: false,
    });
  }

  // Format month name
  const monthName = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(firstDayOfMonth);

  return { year, month, days, monthName };
}

/**
 * Navigate to previous/next month.
 * Returns new year/month values with proper rollover handling.
 */
export function navigateMonth(
  year: number,
  month: number,
  direction: 'prev' | 'next',
): { year: number; month: number } {
  if (direction === 'next') {
    if (month === 11) {
      return { year: year + 1, month: 0 };
    }
    return { year, month: month + 1 };
  } else {
    if (month === 0) {
      return { year: year - 1, month: 11 };
    }
    return { year, month: month - 1 };
  }
}

/**
 * Format date for display (e.g., "Sun, Dec 9").
 */
export function formatDateShort(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Format date for accessibility label (e.g., "Sunday, December 9, 2024").
 */
export function formatDateForA11y(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Get the year and month from a Date object.
 */
export function getYearMonth(date: Date): { year: number; month: number } {
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
  };
}

/**
 * Create a Date from year and month (first day of month).
 */
export function createMonthDate(year: number, month: number): Date {
  return new Date(year, month, 1);
}

/**
 * Check if navigation to previous month should be blocked.
 * Returns true if we're at or before the current month.
 */
export function canNavigatePrev(year: number, month: number, timezone: string): boolean {
  return !isCurrentMonth(year, month, timezone) && !isPastMonth(year, month, timezone);
}
